/**
 * useModelStatistics Hook
 * 
 * React hook for managing model statistics including element counts
 * Automatically updates when the model changes or loads
 */

import { useState, useEffect, useCallback } from 'react';
import { useViewerStore } from '@/store/viewerStore';
import { ModelStatisticsService, type ModelStatistics } from '@/services/core/modelStatistics';

export function useModelStatistics() {
  const { viewer, isModelLoaded } = useViewerStore();
  const [statistics, setStatistics] = useState<ModelStatistics>({
    totalElements: 0,
    leafElements: 0,
    totalCategories: 0,
    isLoaded: false
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadStatistics = useCallback(async () => {
    if (!viewer || !isModelLoaded) {
      setStatistics({
        totalElements: 0,
        leafElements: 0,
        totalCategories: 0,
        isLoaded: false
      });
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const stats = await ModelStatisticsService.getModelStatistics(viewer);
      setStatistics(stats);
      console.log('Model statistics loaded:', stats);
    } catch (err) {
      console.error('Failed to load model statistics:', err);
      setError(err instanceof Error ? err.message : 'Failed to load model statistics');
      setStatistics({
        totalElements: 0,
        leafElements: 0,
        totalCategories: 0,
        isLoaded: false
      });
    } finally {
      setIsLoading(false);
    }
  }, [viewer, isModelLoaded]);

  // Load statistics when model loads or changes
  useEffect(() => {
    if (isModelLoaded && viewer) {
      // Add a small delay to ensure the model is fully loaded
      const timer = setTimeout(() => {
        loadStatistics();
      }, 500);

      return () => clearTimeout(timer);
    } else {
      // Reset statistics when no model is loaded
      setStatistics({
        totalElements: 0,
        leafElements: 0,
        totalCategories: 0,
        isLoaded: false
      });
      setError(null);
    }
  }, [isModelLoaded, viewer, loadStatistics]);

  // Listen for geometry loaded event to ensure we have the instance tree
  useEffect(() => {
    if (!viewer) return;

    const onGeometryLoaded = () => {
      loadStatistics();
    };

    const onObjectTreeCreated = () => {
      loadStatistics();
    };

    viewer.addEventListener(Autodesk.Viewing.GEOMETRY_LOADED_EVENT, onGeometryLoaded);
    viewer.addEventListener(Autodesk.Viewing.OBJECT_TREE_CREATED_EVENT, onObjectTreeCreated);

    return () => {
      viewer.removeEventListener(Autodesk.Viewing.GEOMETRY_LOADED_EVENT, onGeometryLoaded);
      viewer.removeEventListener(Autodesk.Viewing.OBJECT_TREE_CREATED_EVENT, onObjectTreeCreated);
    };
  }, [viewer, loadStatistics]);

  const refreshStatistics = useCallback(() => {
    loadStatistics();
  }, [loadStatistics]);

  return {
    statistics,
    isLoading,
    error,
    refreshStatistics
  };
}